import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppIconComponent } from './app-icon.component';

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  type?: 'text' | 'number' | 'date' | 'custom';
  align?: 'left' | 'center' | 'right';
}

export interface TableConfig {
  selectable?: boolean;
  stickyHeader?: boolean;
  showSort?: boolean;
  showPaginator?: boolean;
}

/**
 * AppTableComponent - Microcomponente de tabela
 * Substitui mat-table com implementação em Tailwind CSS
 *
 * @example
 * <app-table [data]="data" [columns]="columns" [config]="config" />
 */
@Component({
  selector: 'app-table',
  standalone: true,
  imports: [CommonModule, AppIconComponent],
  template: `
    <div class="app-table-container">
      @if (loading()) {
      <div class="flex flex-col items-center justify-center py-12">
        <div
          class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"
        ></div>
        <p class="text-gray-600">{{ loadingMessage() }}</p>
      </div>
      } @else if (data().length === 0) {
      <div class="flex flex-col items-center justify-center py-12">
        <app-icon name="inbox" [size]="48" class="text-gray-400 mb-4" />
        <p class="text-gray-600">{{ emptyMessage() }}</p>
      </div>
      } @else {
      <div class="overflow-x-auto">
        <table
          class="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm"
        >
          <!-- Header -->
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              @if (config().selectable) {
              <th class="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  [checked]="isAllSelected()"
                  [indeterminate]="
                    selectedRows().length > 0 && !isAllSelected()
                  "
                  (change)="toggleAllRows()"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </th>
              } @for (column of columns(); track column.key) {
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                [class.cursor-pointer]="config().showSort && column.sortable"
                [class.hover:bg-gray-100]="config().showSort && column.sortable"
                [style.width]="column.width"
                [style.text-align]="column.align || 'left'"
                (click)="onSort(column)"
              >
                <div class="flex items-center gap-2">
                  <span>{{ column.label }}</span>
                  @if (config().showSort && column.sortable) {
                  <app-icon
                    [name]="getSortIcon(column.key)"
                    [size]="16"
                    class="text-gray-400"
                  />
                  }
                </div>
              </th>
              } @if (showActions()) {
              <th
                class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Ações
              </th>
              }
            </tr>
          </thead>

          <!-- Body -->
          <tbody class="bg-white divide-y divide-gray-200">
            @for (row of data(); track row; let i = $index) {
            <tr
              class="hover:bg-gray-50 transition-colors duration-150"
              [class.bg-blue-50]="isRowSelected(row)"
              (click)="onRowClick(row)"
            >
              @if (config().selectable) {
              <td class="px-6 py-4">
                <input
                  type="checkbox"
                  [checked]="isRowSelected(row)"
                  (change)="toggleRow(row)"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </td>
              } @for (column of columns(); track column.key) {
              <td
                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                [style.text-align]="column.align || 'left'"
              >
                @if (column.type === 'custom') {
                <ng-content select="[slot=custom-cell]"></ng-content>
                } @else {
                {{ getCellValue(row, column) }}
                }
              </td>
              } @if (showActions()) {
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <ng-content select="[slot=actions]"></ng-content>
              </td>
              }
            </tr>
            }
          </tbody>
        </table>
      </div>
      }
    </div>
  `,
  styles: [],
})
export class AppTableComponent {
  @Input() data = signal<any[]>([]);
  @Input() columns = signal<TableColumn[]>([]);
  @Input() config = signal<TableConfig>({});
  @Input() loading = signal(false);
  @Input() loadingMessage = signal('Carregando...');
  @Input() emptyMessage = signal('Nenhum item encontrado');
  @Input() selectedRows = signal<any[]>([]);
  @Input() sortField = signal<string>('');
  @Input() sortDirection = signal<'asc' | 'desc'>('asc');
  @Input() showActions = signal(false);

  @Output() rowClick = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<{
    field: string;
    direction: 'asc' | 'desc';
  }>();
  @Output() selectionChange = new EventEmitter<any[]>();

  isAllSelected = computed(() => {
    return (
      this.data().length > 0 &&
      this.selectedRows().length === this.data().length
    );
  });

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selectedRows.set([]);
    } else {
      this.selectedRows.set([...this.data()]);
    }
    this.selectionChange.emit(this.selectedRows());
  }

  isRowSelected(row: any): boolean {
    return this.selectedRows().includes(row);
  }

  toggleRow(row: any): void {
    const selected = this.selectedRows();
    const index = selected.indexOf(row);

    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(row);
    }

    this.selectedRows.set([...selected]);
    this.selectionChange.emit(this.selectedRows());
  }

  onRowClick(row: any): void {
    this.rowClick.emit(row);
  }

  onSort(column: TableColumn): void {
    if (!column.sortable || !this.config().showSort) {
      return;
    }

    let direction: 'asc' | 'desc' = 'asc';

    if (this.sortField() === column.key) {
      direction = this.sortDirection() === 'asc' ? 'desc' : 'asc';
    }

    this.sortField.set(column.key);
    this.sortDirection.set(direction);
    this.sortChange.emit({ field: column.key, direction });
  }

  getSortIcon(columnKey: string): string {
    if (this.sortField() !== columnKey) {
      return 'swap_vert';
    }
    return this.sortDirection() === 'asc'
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  getCellValue(row: any, column: TableColumn): any {
    const value = row[column.key];

    if (column.type === 'date' && value) {
      return new Date(value).toLocaleDateString('pt-BR');
    }

    if (column.type === 'number' && value !== null && value !== undefined) {
      return typeof value === 'number' ? value.toLocaleString('pt-BR') : value;
    }

    return value || '-';
  }
}
